import { supabase } from "@/integrations/supabase/client";
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { UserRole } from '@/types/auth';
import { useRedirect } from '@/contexts/AuthContext';
import { useState, useEffect, useCallback } from 'react';

export const useAuthMethods = (fetchUserRole: (userId: string) => Promise<void>) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { redirectPath, setRedirectPath } = useRedirect();
  const [googleAuthInProgress, setGoogleAuthInProgress] = useState(false);

  // Check for session when redirected back from Google
  useEffect(() => {
    const handleGoogleRedirect = async () => {
      // Check if we have OAuth callback markers in the URL
      const isOAuthCallback = 
        (window.location.hash && (window.location.hash.includes('access_token') || window.location.hash.includes('error'))) || 
        (window.location.search && (window.location.search.includes('code=') || window.location.search.includes('error=')));
      
      if (isOAuthCallback) {
        console.log("useAuthMethods: Detected OAuth callback params in URL", {
          hash: window.location.hash,
          search: window.location.search
        });
        setGoogleAuthInProgress(true);
        
        try {
          // Clear hash and search params to prevent re-processing
          if (window.history && window.history.replaceState) {
            window.history.replaceState({}, document.title, window.location.pathname);
          }
          
          // The session is automatically exchanged by Supabase when it detects these params
          const { data, error } = await supabase.auth.getSession();
          
          if (data.session && !error) {
            // Successfully authenticated
            console.log("Google auth successful, fetching user role");
            await fetchUserRole(data.session.user.id);
            
            toast({
              title: "Signed in successfully",
              description: "Welcome to InstaMG!",
            });
            
            if (redirectPath) {
              navigate(redirectPath);
              setRedirectPath(null);
            } else {
              navigate('/');
            }
          } else if (error) {
            console.error("Google auth error:", error);
            toast({
              title: "Authentication Error",
              description: error.message || "Failed to complete Google authentication",
              variant: "destructive",
            });
          }
          setGoogleAuthInProgress(false);
        } catch (error) {
          console.error("Exception during Google auth redirect handling:", error);
          setGoogleAuthInProgress(false);
          
          toast({
            title: "Authentication Error",
            description: "Failed to complete Google authentication",
            variant: "destructive",
          });
        }
      }
    };

    handleGoogleRedirect();
  }, []); // Run once on component mount, no dependencies to avoid re-runs

  // Initialize Google One Tap when component mounts
  useEffect(() => {
    // Only initialize if Google One Tap script isn't already loaded
    if (!window.google && !document.querySelector('script[src*="accounts.google.com/gsi/client"]')) {
      const script = document.createElement('script');
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = initializeOneTap;
      document.head.appendChild(script);
    } else if (window.google) {
      // If Google is already loaded, initialize One Tap
      initializeOneTap();
    }

    return () => {
      // Clean up One Tap when component unmounts
      if (window.google?.accounts?.id) {
        window.google.accounts.id.cancel();
      }
    };
  }, []);

  // Initialize Google One Tap
  const initializeOneTap = useCallback(() => {
    if (window.google?.accounts?.id) {
      // Check if user is already signed in
      supabase.auth.getSession().then(({ data }) => {
        if (!data.session) {
          // Only show One Tap if user is not signed in
          window.google.accounts.id.initialize({
            client_id: '479391100706-3but245sr4blkgi86du3kh1upm40ik1s.apps.googleusercontent.com',
            callback: handleGoogleOneTapResponse,
            auto_select: true,
            cancel_on_tap_outside: true
          });
          
          window.google.accounts.id.prompt((notification) => {
            if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
              console.log('One Tap not displayed:', notification.getNotDisplayedReason() || notification.getSkippedReason());
            }
          });
        }
      });
    }
  }, []);

  // Handle Google One Tap response
  const handleGoogleOneTapResponse = async (response: any) => {
    try {
      setGoogleAuthInProgress(true);
      toast({
        title: "Processing",
        description: "Authenticating with Google...",
      });
      
      const { data, error } = await supabase.auth.signInWithIdToken({
        provider: 'google',
        token: response.credential,
      });
      
      if (error) {
        console.error('Google One Tap error:', error);
        toast({
          title: "Authentication Error",
          description: error.message || "Failed to authenticate with Google One Tap",
          variant: "destructive",
        });
        setGoogleAuthInProgress(false);
        return;
      }
      
      if (data.user) {
        console.log("Google One Tap successful, fetching user role");
        await fetchUserRole(data.user.id);
        
        toast({
          title: "Signed in successfully",
          description: "Welcome to InstaMG!",
        });
        
        if (redirectPath) {
          navigate(redirectPath);
          setRedirectPath(null);
        } else {
          navigate('/');
        }
      }
      
      setGoogleAuthInProgress(false);
    } catch (error: any) {
      console.error('Google One Tap exception:', error);
      toast({
        title: "Authentication Error",
        description: "Failed to authenticate with Google One Tap",
        variant: "destructive",
      });
      setGoogleAuthInProgress(false);
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      console.log("Attempting sign in with email:", email);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      
      if (error) {
        console.error("Sign in error:", error);
        toast({
          title: "Sign in failed",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }
      
      console.log("Sign in successful:", data);
      toast({
        title: "Signed in successfully",
        description: "Welcome back!",
      });

      const { data: userData } = await supabase.auth.getUser();
      if (userData.user) {
        await fetchUserRole(userData.user.id);
        
        const userMetadata = userData.user.user_metadata;
        
        // Determine where to navigate after login
        if (redirectPath) {
          navigate(redirectPath);
          setRedirectPath(null);
        } else if (userMetadata && userMetadata.role === 'admin') {
          navigate('/admin/dashboard');
        } else {
          navigate('/');
        }
      }

      return { error: null };
    } catch (error: any) {
      console.error("Sign in exception:", error);
      toast({
        title: "Sign in failed",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string, phone: string, role: UserRole = 'customer') => {
    try {
      console.log("Attempting sign up with email:", email);
      const { error } = await supabase.auth.signUp({ 
        email, 
        password,
        options: {
          data: {
            first_name: firstName,
            last_name: lastName,
            phone: phone,
            role: role
          },
        },
      });

      if (error) {
        console.error("Sign up error:", error);
        toast({
          title: "Sign up failed",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }

      console.log("Sign up successful");
      toast({
        title: "Account created",
        description: "Welcome to InstaMG!",
      });
      
      return { error: null };
    } catch (error: any) {
      console.error("Sign up exception:", error);
      toast({
        title: "Sign up failed",
        description: error.message,
        variant: "destructive",
      });
      return { error };
    }
  };

  // Update the Google sign-in button method to use the exact same approach that works with One Tap
  const signInWithGoogle = async () => {
    try {
      console.log('Initiating Google sign in button flow...');
      setGoogleAuthInProgress(true);
      
      // Use the same configuration as what works with One Tap
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth`,
          queryParams: {
            access_type: 'offline',
            prompt: 'select_account'
          }
        }
      });
      
      if (error) {
        console.error('Google auth error:', error);
        toast({
          title: "Google Sign In Failed",
          description: error.message,
          variant: "destructive",
        });
        setGoogleAuthInProgress(false);
        return { error };
      }
      
      // Log the redirect URL for debugging
      console.log('Google auth initiated, redirect URL:', data.url);
      
      // signInWithOAuth will handle the redirect automatically
      return { error: null };
    } catch (error: any) {
      console.error('Google auth exception:', error);
      toast({
        title: "Authentication Error",
        description: "There was an error authenticating with Google",
        variant: "destructive",
      });
      setGoogleAuthInProgress(false);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      console.log("Signing out");
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.error("Sign out error:", error);
        toast({
          title: "Sign out failed",
          description: error.message || "There was an error signing out.",
          variant: "destructive",
        });
        return;
      }
      
      // Clear local auth state and redirect
      navigate('/auth');
      toast({
        title: "Signed out",
        description: "You have been signed out successfully.",
      });
    } catch (error: any) {
      console.error("Sign out exception:", error);
      toast({
        title: "Sign out failed",
        description: error.message || "There was an error signing out.",
      });
    }
  };

  return { 
    signIn, 
    signUp, 
    signInWithGoogle, 
    signOut, 
    googleAuthInProgress 
  };
};

// Extend window global interface to include Google One Tap
declare global {
  interface Window {
    google?: {
      accounts?: {
        id?: {
          initialize: (config: any) => void;
          prompt: (callback: (notification: any) => void) => void;
          renderButton: (element: HTMLElement, options: any) => void;
          cancel: () => void;
        }
      }
    }
  }
}
